/* assets/ctr-bombi-blast-buy-box.js
   <ctrbb-buy-box> — Bombi Blast LP buy box (section ctr-bombi-blast-buy-box).
   - Gallery: thumbnails switch the main image (slides are server-rendered, JS toggles `hidden`).
   - Setup cards + purchase mode are native radios; CSS styles the checked state.
   - Subscribe line ("$35 (SAVE $9)") follows the selected setup.
   - All buy boxes on the page stay in sync (setup index + mode) through the 'ctrbb:sync' event.
   - Add to cart: POST /cart/add.js with the main line (+ selling_plan when subscribing)
     and, for bundles, the gift as a separate ONE-TIME line; then the theme's mini-cart events. */
(function () {
  'use strict';
  if (customElements.get('ctrbb-buy-box')) return;

  const SYNC = 'ctrbb:sync';

  class CtrbbBuyBox extends HTMLElement {
    connectedCallback() {
      this.initGallery();
      this.initForm();
      this.onSync = (e) => this.applySync(e.detail);
      document.addEventListener(SYNC, this.onSync);
    }

    disconnectedCallback() {
      document.removeEventListener(SYNC, this.onSync);
    }

    /* ---------------- Gallery ---------------- */
    initGallery() {
      this.slides = Array.from(this.querySelectorAll('[data-ctrbb-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-ctrbb-thumb]'));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showSlide(parseInt(thumb.dataset.ctrbbThumb, 10)));
      });
    }

    showSlide(i) {
      this.slides.forEach((slide, n) => {
        slide.hidden = n !== i;
        slide.classList.toggle('is-active', n === i);
      });
      this.thumbs.forEach((thumb, n) => {
        thumb.classList.toggle('is-active', n === i);
        if (n === i) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    /* ---------------- Form ---------------- */
    initForm() {
      this.form = this.querySelector('[data-ctrbb-form]');
      if (!this.form) return;
      this.submit = this.form.querySelector('[data-ctrbb-submit]');
      this.submitLabel = this.form.querySelector('[data-ctrbb-submit-label]');
      this.error = this.form.querySelector('[data-ctrbb-error]');
      this.subLine = this.form.querySelector('[data-ctrbb-sub-line]');

      if (!this.setupInput()) {
        const first = this.form.querySelector('.ctrbbx__setup-input:not(:disabled)');
        if (first) first.checked = true;
      }
      this.form.addEventListener('change', (e) => {
        if (this.error) this.error.hidden = true;
        this.update();
        if (!this.syncing && e.isTrusted !== false) this.broadcast();
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
      this.update();
    }

    setupInput() {
      return this.form.querySelector('.ctrbbx__setup-input:checked');
    }

    mode() {
      const m = this.form.querySelector('input[name^="mode-"]:checked');
      return m ? m.value : 'onetime';
    }

    update() {
      const input = this.setupInput();
      if (this.subLine) {
        let text = '';
        if (input && input.dataset.sub) {
          const fmt = input.dataset.subSave ? this.subLine.dataset.format : this.subLine.dataset.formatNosave;
          text = (fmt || '[price]')
            .replace('[price]', input.dataset.sub)
            .replace('[save]', input.dataset.subSave || '');
        }
        this.subLine.textContent = text;
      }
      this.updateCardPrices();
      if (this.submit && !this.isAdding) this.submit.disabled = !input || input.disabled;
    }

    /* Every card shows the price of the active purchase mode (subscribe → plan price + struck regular). */
    updateCardPrices() {
      const subscribe = this.mode() === 'subscribe';
      this.form.querySelectorAll('.ctrbbx__setup-input').forEach((inp) => {
        const card = inp.closest('.ctrbbx__setup');
        if (!card) return;
        const useSub = subscribe && !!inp.dataset.sub;
        const price = useSub ? inp.dataset.sub : inp.dataset.onetime;
        const compare = useSub ? inp.dataset.cardSubCompare : inp.dataset.cardOnetimeCompare;
        const save = useSub ? inp.dataset.cardSubSave : inp.dataset.cardOnetimeSave;
        const priceEl = card.querySelector('[data-ctrbb-card-price]');
        const cmpEl = card.querySelector('[data-ctrbb-card-compare]');
        const cmpAmount = card.querySelector('[data-ctrbb-card-compare-amount]');
        const saveEl = card.querySelector('[data-ctrbb-card-save]');
        if (priceEl && price) priceEl.textContent = price;
        if (cmpEl) {
          cmpEl.hidden = !compare;
          if (cmpAmount && compare) cmpAmount.textContent = compare;
        }
        if (saveEl) {
          saveEl.hidden = !save;
          if (save) saveEl.textContent = (saveEl.dataset.format || 'SAVE [amount]').replace('[amount]', save);
        }
      });
    }

    /* ---------------- Sync between buy boxes ---------------- */
    broadcast() {
      const input = this.setupInput();
      document.dispatchEvent(new CustomEvent(SYNC, {
        detail: { source: this, index: input ? input.dataset.index : null, mode: this.mode() },
      }));
    }

    applySync(detail) {
      if (!detail || detail.source === this || !this.form) return;
      this.syncing = true;
      if (detail.index !== null) {
        const target = this.form.querySelector('.ctrbbx__setup-input[data-index="' + detail.index + '"]');
        if (target && !target.disabled) target.checked = true;
      }
      const mode = this.form.querySelector('input[name^="mode-"][value="' + detail.mode + '"]');
      if (mode && !mode.disabled) mode.checked = true;
      this.update();
      this.syncing = false;
    }

    /* ---------------- Add to cart ---------------- */
    setAdding(adding) {
      this.isAdding = adding;
      if (!this.submit) return;
      this.submit.disabled = adding;
      this.submit.setAttribute('aria-busy', adding ? 'true' : 'false');
      if (this.submitLabel) {
        this.submitLabel.textContent = adding ? this.submit.dataset.addingLabel : this.submit.dataset.defaultLabel;
      }
    }

    async addToCart() {
      const input = this.setupInput();
      if (!input || input.disabled || this.isAdding) return;

      const item = {
        id: parseInt(input.dataset.variantId, 10),
        quantity: parseInt(input.dataset.quantity, 10) || 1,
      };
      if (this.mode() === 'subscribe') {
        if (!input.dataset.sellingPlan) {
          // No Skio plan on this product yet — don't silently add a one-time purchase.
          console.warn('[ctrbb-buy-box] No selling plan for "' + input.dataset.title + '" — set up the Skio plan.');
          if (this.error) {
            this.error.textContent = this.error.dataset.noPlan || 'Subscription is not available yet.';
            this.error.hidden = false;
          }
          return;
        }
        item.selling_plan = parseInt(input.dataset.sellingPlan, 10);
      }
      // Skio Classic Box V2 (static box, e.g. bombi-blast-box): ONE parent line + contents as properties.
      // Same contract as the live Premium Bundle / ctr-variety-packs-offer2.
      if (input.dataset.boxContents) {
        let contents = [];
        try { contents = JSON.parse(input.dataset.boxContents) || []; } catch (e) { contents = []; }
        if (contents.length) {
          const props = { _isClassicBoxV2: true };
          contents.forEach((c) => {
            const q = Number(c.q) || 1;
            props[c.t] = (props[c.t] || 0) + q;
            const key = '_pvgid://shopify/ProductVariant/' + c.v;
            props[key] = (props[key] || 0) + q;
          });
          item.properties = props;
        }
      }
      const giftId = input.dataset.giftVariantId ? parseInt(input.dataset.giftVariantId, 10) : null;

      this.setAdding(true);
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.description || body.message || 'Could not add to cart (' + res.status + ')');
        }
        const added = await res.json().catch(() => ({}));

        // Gift (Limited Edition Bottle): ONE free one-time line, linked to the parent, never on the
        // subscription. Routed through the mini cart's addFreeBonusItems() (like Premium Bundle) so a
        // second bundle replaces the existing gift line instead of adding another one.
        // Isolated try/catch: the main item is already in the cart at this point.
        if (giftId) {
          try {
            const store = window.Alpine && typeof window.Alpine.store === 'function' ? window.Alpine.store('cart') : null;
            if (store && typeof store.addFreeBonusItems === 'function') {
              await store.loadCart();
              let parentKey = added && added.items && added.items[0] ? added.items[0].key : null;
              if (!parentKey && store.items && store.items.length) {
                const line = store.items.slice().reverse().find((l) => Number(l.variant_id) === item.id);
                parentKey = line ? line.key : null;
              }
              await store.addFreeBonusItems([{ id: giftId, quantity: 1 }], item.id, parentKey);
            } else {
              await fetch(root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: [{ id: giftId, quantity: 1, properties: { _free_gift_parent_id: String(item.id) } }] }),
              });
            }
          } catch (giftErr) {
            console.error('[ctrbb-buy-box] gift add failed (main item was added):', giftErr);
          }
        }

        // Same events as the other Reboot buy boxes → Alpine mini cart refreshes + opens.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:open', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:toast', {
          detail: { type: 'success', message: (input.dataset.title || 'Item') + ' added to cart' },
        }));
        if (window.theme && window.theme.cart && typeof window.theme.cart.getCart === 'function') {
          window.theme.cart.getCart();
        }
      } catch (err) {
        console.error('[ctrbb-buy-box] add to cart failed:', err);
        if (this.error) {
          this.error.textContent = err && err.message ? err.message : 'Something went wrong. Please try again.';
          this.error.hidden = false;
        }
      } finally {
        this.setAdding(false);
        this.update();
      }
    }
  }

  customElements.define('ctrbb-buy-box', CtrbbBuyBox);
})();
